import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Recycle, ArrowLeft, Info, TrendingDown, 
  HelpCircle, Download, Scale, Lightbulb, Save, History,
  Droplets, Car, Settings, Package
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import NotificationBell from '../components/NotificationBell';
import apiClient from '../api/client';

// 2026 m. kainos pagal organizacijas (EUR už toną)
const ORGANIZATION_PRICES = {
  gamtosAteitis: {
    name: "Gamtos Ateitis",
    website: "https://gamtosateitis.lt",
    pricing: {
      glass: { price: 190, unit: 'tiekta', duty: 70, name: 'Stiklinė pakuotė' },
      paper: { price: 289, unit: 'tiekta', duty: 83, name: 'Popierinė pakuotė' },
      plastic: { price: 540, unit: 'tvarkoma', duty: 90, name: 'Plastikinė pakuotė' },
      pet: { price: 790, unit: 'tvarkoma', duty: 90, name: 'PET pakuotė' },
      wood: { price: 40, unit: 'tiekta', duty: 35, name: 'Medinė pakuotė' },
      combined: { price: 952, unit: 'tvarkoma', duty: 45, name: 'Kombinuota pakuotė' },
      metalBlack: { price: 375, unit: 'tiekta', duty: 75, name: 'Metalinė (juodasis)' },
      metalAluminum: { price: 120, unit: 'tiekta', duty: 55, name: 'Metalinė (aliuminis)' },
      other: { price: 428, unit: 'tiekta', duty: 25, name: 'Kita pakuotė' },
      // Neperdirbamos
      paperNonRecyclable: { price: 992, unit: 'tiekta', duty: 100, name: 'Popierinė (neperdirbama)' },
      plasticNonRecyclable: { price: 992, unit: 'tiekta', duty: 100, name: 'Plastikinė (neperdirbama)' },
      petNonRecyclable: { price: 992, unit: 'tiekta', duty: 100, name: 'PET (neperdirbama)' },
      combinedNonRecyclable: { price: 992, unit: 'tiekta', duty: 100, name: 'Kombinuota (neperdirbama)' },
      woodNonRecyclable: { price: 992, unit: 'tiekta', duty: 100, name: 'Medinė (neperdirbama)' },
      otherNonRecyclable: { price: 992, unit: 'tiekta', duty: 100, name: 'Kita (neperdirbama)' },
    }
  },
  zaliasisTaskas: {
    name: "Žaliasis Taškas",
    website: "https://www.zaliasistaskas.lt",
    pricing: {
      glass: { price: 276, unit: 'tvarkoma', duty: 70, name: 'Stiklinė pakuotė' },
      paper: { price: 352, unit: 'tvarkoma', duty: 83, name: 'Popierinė pakuotė' },
      plastic: { price: 493, unit: 'tvarkoma', duty: 65, name: 'Plastikinė pakuotė' },
      pet: { price: 797, unit: 'tvarkoma', duty: 65, name: 'PET pakuotė' },
      wood: { price: 119, unit: 'tvarkoma', duty: 35, name: 'Medinė pakuotė' },
      combined: { price: 952, unit: 'tvarkoma', duty: 30, name: 'Kombinuota pakuotė' },
      metalBlack: { price: 517, unit: 'tvarkoma', duty: 75, name: 'Metalinė (juodasis)' },
      metalAluminum: { price: 224, unit: 'tvarkoma', duty: 55, name: 'Metalinė (aliuminis)' },
      other: { price: 992, unit: 'tvarkoma', duty: 25, name: 'Kita pakuotė' },
      // Neperdirbamos
      paperNonRecyclable: { price: 992, unit: 'tvarkoma', duty: 100, name: 'Popierinė (neperdirbama)' },
      plasticNonRecyclable: { price: 992, unit: 'tvarkoma', duty: 100, name: 'Plastikinė (neperdirbama)' },
      petNonRecyclable: { price: 992, unit: 'tvarkoma', duty: 100, name: 'PET (neperdirbama)' },
      combinedNonRecyclable: { price: 992, unit: 'tvarkoma', duty: 100, name: 'Kombinuota (neperdirbama)' },
      woodNonRecyclable: { price: 992, unit: 'tvarkoma', duty: 100, name: 'Medinė (neperdirbama)' },
      otherNonRecyclable: { price: 992, unit: 'tvarkoma', duty: 100, name: 'Kita (neperdirbama)' },
    }
  }
};

// Taršos mokesčio tarifai 2026 m. (EUR už toną, su koeficientu 1.48)
const POLLUTION_TAX = {
  glass: { base: 279, withCoef: 412.92 },
  paper: { base: 133, withCoef: 196.84 },
  plastic: { base: 520, withCoef: 769.60 },
  pet: { base: 520, withCoef: 769.60 },
  wood: { base: 189, withCoef: 279.72 },
  combined: { base: 520, withCoef: 769.60 },
  metalBlack: { base: 186, withCoef: 275.28 },
  metalAluminum: { base: 186, withCoef: 275.28 },
  other: { base: 299, withCoef: 442.52 },
  // Neperdirbamos
  paperNonRecyclable: { base: 188, withCoef: 278.24 },
  plasticNonRecyclable: { base: 875, withCoef: 1295.00 },
  petNonRecyclable: { base: 875, withCoef: 1295.00 },
  combinedNonRecyclable: { base: 1200, withCoef: 1776.00 },
  woodNonRecyclable: { base: 225, withCoef: 333.00 },
  otherNonRecyclable: { base: 423, withCoef: 626.04 },
};

// ============================================================
// GIA – Gamintojų ir importuotojų asocijacija
// 2025 m. kainos (EUR už tiektą toną į LR vidaus rinką)
// Šaltinis: https://gia.lt/gamintojams-ir-importuotojams/skaiciuokle/
// ============================================================
const GIA_PRICES = {
  oils:           { price: 6.00,   name: 'Alyvos (variklo, pavarų, pramoninė)' },
  tiresLight:     { price: 148.16, name: 'Padangos – motociklų ir lengvųjų automobilių' },
  tiresHeavy:     { price: 165.11, name: 'Padangos – autobusų, krovininių, žemės ūkio (>3 kg)' },
  filtersFuel:    { price: 622.87, name: 'Filtrai – degalų arba tepalų' },
  filtersAir:     { price: 623.77, name: 'Filtrai – įsiurbiamo oro' },
  shockAbsorbers: { price: 336.15, name: 'Amortizatoriai' },
};

// Metinis administravimo mokestis (EUR be PVM)
const GIA_ADMIN_FEE = [
  { from: 0,     to: 200,   fee: 88 },
  { from: 201,   to: 400,   fee: 160 },
  { from: 401,   to: 800,   fee: 220 },
  { from: 801,   to: 1600,  fee: 280 },
  { from: 1601,  to: 3200,  fee: 480 },
  { from: 3201,  to: 6400,  fee: 720 },
  { from: 6401,  to: 10000, fee: 1100 },
  { from: 10001, to: Infinity, fee: 1540 },
];

const calcGiaAdminFee = (total) => {
  if (total <= 200) return 88;
  if (total <= 400) return 160;
  if (total <= 800) return 220;
  if (total <= 1600) return 280;
  if (total <= 3200) return 480;
  if (total <= 6400) return 720;
  if (total <= 10000) return 1100;
  return 1540;
};

const GIA_PRODUCT_TYPES = Object.entries(GIA_PRICES).map(([id, v]) => ({ id, ...v }));

const RECYCLABLE_TYPES = [
  { id: 'glass', name: 'Stiklinė pakuotė' },
  { id: 'paper', name: 'Popierinė / kartoninė' },
  { id: 'plastic', name: 'Plastikinė pakuotė' },
  { id: 'pet', name: 'PET pakuotė' },
  { id: 'wood', name: 'Medinė pakuotė' },
  { id: 'combined', name: 'Kombinuota pakuotė' },
  { id: 'metalBlack', name: 'Metalinė (juodasis metalas)' },
  { id: 'metalAluminum', name: 'Metalinė (aliuminis)' },
  { id: 'other', name: 'Kita pakuotė' },
];

const NON_RECYCLABLE_TYPES = [
  { id: 'paperNonRecyclable', name: 'Popierinė (neperdirbama)' },
  { id: 'plasticNonRecyclable', name: 'Plastikinė (neperdirbama)' },
  { id: 'petNonRecyclable', name: 'PET (neperdirbama)' },
  { id: 'combinedNonRecyclable', name: 'Kombinuota (neperdirbama)' },
  { id: 'woodNonRecyclable', name: 'Medinė (neperdirbama)' },
  { id: 'otherNonRecyclable', name: 'Kita (neperdirbama)' },
];

const PackagingCalculator = () => {
  const { user, logout } = useAuth();
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  
  // Kiekiai pagal pakuotės tipą (tonomis)
  const [quantities, setQuantities] = useState({
    glass: '', paper: '', plastic: '', pet: '', wood: '',
    combined: '', metalBlack: '', metalAluminum: '', other: '',
    paperNonRecyclable: '', plasticNonRecyclable: '', petNonRecyclable: '',
    combinedNonRecyclable: '', woodNonRecyclable: '', otherNonRecyclable: '',
  });

  // GIA – apmokestinamieji gaminiai kiekiai (tonomis)
  const [giaQuantities, setGiaQuantities] = useState({
    oils: '', tiresLight: '', tiresHeavy: '', filtersFuel: '', filtersAir: '', shockAbsorbers: '',
  });

  const handleQuantityChange = (type, value) => {
    setQuantities(prev => ({ ...prev, [type]: value }));
  };

  const handleGiaQuantityChange = (type, value) => {
    setGiaQuantities(prev => ({ ...prev, [type]: value }));
  };

  // GIA skaičiavimas
  const giaResults = useMemo(() => {
    let subtotal = 0;
    const details = [];
    GIA_PRODUCT_TYPES.forEach(({ id, name, price }) => {
      const qty = parseFloat(giaQuantities[id]) || 0;
      if (qty > 0) {
        const cost = qty * price;
        subtotal += cost;
        details.push({ id, name, qty, price, cost });
      }
    });
    const adminFee = subtotal > 0 ? calcGiaAdminFee(subtotal) : 0;
    return { subtotal, adminFee, total: subtotal + adminFee, details };
  }, [giaQuantities]);

  const clearGia = () => {
    setGiaQuantities({ oils: '', tiresLight: '', tiresHeavy: '', filtersFuel: '', filtersAir: '', shockAbsorbers: '' });
  };

  // Skaičiavimo funkcija
  const calculateCost = (orgKey, type, quantity) => {
    if (!quantity || quantity <= 0) return 0;
    const org = ORGANIZATION_PRICES[orgKey];
    const pricing = org.pricing[type];
    if (!pricing) return 0;
    
    if (pricing.unit === 'tiekta') {
      return quantity * pricing.price;
    } else {
      return quantity * (pricing.duty / 100) * pricing.price;
    }
  };

  // Taršos mokestis
  const calculatePollutionTax = (type, quantity, orgKey) => {
    if (!quantity || quantity <= 0) return 0;
    const tax = POLLUTION_TAX[type];
    const org = ORGANIZATION_PRICES[orgKey];
    const pricing = org.pricing[type];
    if (!tax || !pricing) return 0;
    
    // Plastikinėms, PET ir kombinuotoms + neperdirbamoms
    if (['plastic', 'pet', 'combined', 'plasticNonRecyclable', 'petNonRecyclable', 'combinedNonRecyclable'].includes(type)) {
      const untreatedPercent = (100 - pricing.duty) / 100;
      return quantity * untreatedPercent * tax.withCoef;
    }
    // Neperdirbamoms kitoms - pilnas mokestis
    if (type.includes('NonRecyclable')) {
      return quantity * tax.withCoef;
    }
    return 0;
  };

  // Rezultatai
  const results = useMemo(() => {
    const gamtosAteitis = { total: 0, tax: 0, details: [] };
    const zaliasisTaskas = { total: 0, tax: 0, details: [] };
    const allTypes = [...RECYCLABLE_TYPES, ...NON_RECYCLABLE_TYPES];

    allTypes.forEach(packType => {
      const qty = parseFloat(quantities[packType.id]) || 0;
      if (qty > 0) {
        const gaCost = calculateCost('gamtosAteitis', packType.id, qty);
        const gaTax = calculatePollutionTax(packType.id, qty, 'gamtosAteitis');
        gamtosAteitis.total += gaCost;
        gamtosAteitis.tax += gaTax;
        gamtosAteitis.details.push({
          type: packType.name, id: packType.id, quantity: qty, cost: gaCost, tax: gaTax,
          pricing: ORGANIZATION_PRICES.gamtosAteitis.pricing[packType.id]
        });

        const ztCost = calculateCost('zaliasisTaskas', packType.id, qty);
        const ztTax = calculatePollutionTax(packType.id, qty, 'zaliasisTaskas');
        zaliasisTaskas.total += ztCost;
        zaliasisTaskas.tax += ztTax;
        zaliasisTaskas.details.push({
          type: packType.name, id: packType.id, quantity: qty, cost: ztCost, tax: ztTax,
          pricing: ORGANIZATION_PRICES.zaliasisTaskas.pricing[packType.id]
        });
      }
    });

    return { gamtosAteitis, zaliasisTaskas };
  }, [quantities]);

  const totalGA = results.gamtosAteitis.total + results.gamtosAteitis.tax;
  const totalZT = results.zaliasisTaskas.total + results.zaliasisTaskas.tax;
  const difference = totalZT - totalGA;
  const cheaperOrg = totalGA <= totalZT ? 'gamtosAteitis' : 'zaliasisTaskas';
  const hasResults = results.gamtosAteitis.details.length > 0;

  // Išsaugoti rezultatus
  const handleSave = async () => {
    if (!user) {
      toast({ title: "Prisijunkite", description: "Norėdami išsaugoti rezultatus, prisijunkite prie paskyros", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      await apiClient.post('/comparison/calculate', { 
        quantities, 
        company_name: user.company_name 
      });
      toast({ title: "Išsaugota!", description: "Palyginimas išsaugotas jūsų istorijoje" });
    } catch (error) {
      toast({ title: "Klaida", description: "Nepavyko išsaugoti", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  // Generuoti PDF
  const handleDownloadPdf = async () => {
    if (!user) {
      toast({ title: "Prisijunkite", description: "Norėdami atsisiųsti PDF, prisijunkite prie paskyros", variant: "destructive" });
      return;
    }
    setGeneratingPdf(true);
    try {
      const response = await apiClient.post('/comparison/pdf', 
        { quantities, company_name: user.company_name },
        { responseType: 'blob' }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pakuociu_palyginimas_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast({ title: "PDF sugeneruotas!", description: "Ataskaita atsisiųsta" });
    } catch (error) {
      toast({ title: "Klaida", description: "Nepavyko sugeneruoti PDF", variant: "destructive" });
    } finally {
      setGeneratingPdf(false);
    }
  };

  const clearAll = () => {
    setQuantities({
      glass: '', paper: '', plastic: '', pet: '', wood: '',
      combined: '', metalBlack: '', metalAluminum: '', other: '',
      paperNonRecyclable: '', plasticNonRecyclable: '', petNonRecyclable: '',
      combinedNonRecyclable: '', woodNonRecyclable: '', otherNonRecyclable: '',
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <Recycle className="w-8 h-8 text-green-600" />
              <span className="text-xl font-bold text-gray-900">taršosmokestis.lt</span>
            </Link>
            
            <div className="flex items-center gap-4">
              {user ? (
                <>
                  <NotificationBell />
                  <span className="text-sm text-gray-600">{user.company_name || user.email}</span>
                  <Button variant="outline" size="sm" onClick={logout}>Atsijungti</Button>
                </>
              ) : (
                <Link to="/login">
                  <Button variant="outline" size="sm">Prisijungti</Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to={user ? "/dashboard" : "/"} className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6">
          <ArrowLeft className="w-4 h-4" />
          Grįžti
        </Link>

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Atliekų tvarkymo skaičiuoklė 2025–2026 m.
          </h1>
          <p className="text-gray-600">
            Palyginkite pakuočių tvarkymo organizacijų kainas ir sužinokite GIA apmokestinamųjų gaminių mokesčius.
          </p>
        </div>

        {/* Top-level tabs: Packaging vs GIA */}
        <Tabs defaultValue="packaging" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2 max-w-md">
            <TabsTrigger value="packaging" className="flex items-center gap-2">
              <Package className="w-4 h-4" />
              Pakuotės
            </TabsTrigger>
            <TabsTrigger value="gia" className="flex items-center gap-2">
              <Settings className="w-4 h-4" />
              GIA – Apmokestinamieji gaminiai
            </TabsTrigger>
          </TabsList>

          {/* ======================================================= */}
          {/* TAB 1: PACKAGING (existing GA vs ZT comparison) */}
          {/* ======================================================= */}
          <TabsContent value="packaging">

        {/* Info Alert */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <h4 className="font-semibold text-blue-900">Svarbi informacija apie kainas</h4>
              <p className="text-sm text-blue-800 mt-1">
                <strong>Gamtos Ateitis</strong> kai kurioms pakuotėms taiko kainą už <em>tiektą toną</em>, 
                o kai kurioms - už <em>tvarkomą toną</em>.
              </p>
              <p className="text-sm text-blue-800 mt-1">
                <strong>Žaliasis Taškas</strong> visoms pakuotėms taiko kainą už <em>tvarkomą toną</em>.
              </p>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Input Form */}
          <div className="lg:col-span-1 space-y-6">
            {/* Perdirbamos pakuotės */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Recycle className="w-5 h-5 text-green-600" />
                  Perdirbamos pakuotės
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {RECYCLABLE_TYPES.map(packType => (
                  <div key={packType.id} className="space-y-1">
                    <Label htmlFor={packType.id} className="text-sm">{packType.name}</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        id={packType.id}
                        type="number"
                        min="0"
                        step="0.001"
                        placeholder="0"
                        value={quantities[packType.id]}
                        onChange={(e) => handleQuantityChange(packType.id, e.target.value)}
                        className="flex-1"
                      />
                      <span className="text-sm text-gray-500 w-8">t</span>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Neperdirbamos pakuotės */}
            <Card className="border-orange-200">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg text-orange-700">
                  <HelpCircle className="w-5 h-5" />
                  Neperdirbamos pakuotės
                </CardTitle>
                <CardDescription className="text-orange-600 text-xs">
                  Už neperdirbamas pakuotes mokamas pilnas taršos mokestis
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {NON_RECYCLABLE_TYPES.map(packType => (
                  <div key={packType.id} className="space-y-1">
                    <Label htmlFor={packType.id} className="text-sm">{packType.name}</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        id={packType.id}
                        type="number"
                        min="0"
                        step="0.001"
                        placeholder="0"
                        value={quantities[packType.id]}
                        onChange={(e) => handleQuantityChange(packType.id, e.target.value)}
                        className="flex-1 border-orange-200"
                      />
                      <span className="text-sm text-gray-500 w-8">t</span>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Button variant="outline" className="w-full" onClick={clearAll}>
              Išvalyti viską
            </Button>
          </div>

          {/* Results */}
          <div className="lg:col-span-2 space-y-6">
            {!hasResults ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Scale className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">Įveskite pakuočių kiekius kairėje, kad pamatytumėte palyginimą</p>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Action Buttons */}
                <div className="flex flex-wrap gap-3">
                  <Button onClick={handleSave} disabled={saving || !user} className="bg-green-600 hover:bg-green-700">
                    <Save className="w-4 h-4 mr-2" />
                    {saving ? 'Saugoma...' : 'Išsaugoti'}
                  </Button>
                  <Button onClick={handleDownloadPdf} disabled={generatingPdf || !user} variant="outline">
                    <Download className="w-4 h-4 mr-2" />
                    {generatingPdf ? 'Generuojama...' : 'Atsisiųsti PDF'}
                  </Button>
                  {user && (
                    <Link to="/dashboard">
                      <Button variant="ghost">
                        <History className="w-4 h-4 mr-2" />
                        Istorija
                      </Button>
                    </Link>
                  )}
                  {!user && (
                    <p className="text-sm text-gray-500 self-center">
                      <Link to="/login" className="text-green-600 hover:underline">Prisijunkite</Link> norėdami išsaugoti ar atsisiųsti PDF
                    </p>
                  )}
                </div>

                {/* Comparison Cards */}
                <div className="grid md:grid-cols-2 gap-4">
                  <Card className={cheaperOrg === 'gamtosAteitis' ? 'ring-2 ring-green-500' : ''}>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg">Gamtos Ateitis</CardTitle>
                        {cheaperOrg === 'gamtosAteitis' && (
                          <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                            Pigiau
                          </span>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Tvarkymo kaina:</span>
                          <span className="font-medium">{results.gamtosAteitis.total.toFixed(2)} €</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Taršos mokestis:</span>
                          <span className="font-medium">{results.gamtosAteitis.tax.toFixed(2)} €</span>
                        </div>
                        <div className="border-t pt-2 flex justify-between">
                          <span className="font-semibold">Viso:</span>
                          <span className="font-bold text-lg">{totalGA.toFixed(2)} €</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className={cheaperOrg === 'zaliasisTaskas' ? 'ring-2 ring-green-500' : ''}>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg">Žaliasis Taškas</CardTitle>
                        {cheaperOrg === 'zaliasisTaskas' && (
                          <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                            Pigiau
                          </span>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Tvarkymo kaina:</span>
                          <span className="font-medium">{results.zaliasisTaskas.total.toFixed(2)} €</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Taršos mokestis:</span>
                          <span className="font-medium">{results.zaliasisTaskas.tax.toFixed(2)} €</span>
                        </div>
                        <div className="border-t pt-2 flex justify-between">
                          <span className="font-semibold">Viso:</span>
                          <span className="font-bold text-lg">{totalZT.toFixed(2)} €</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Savings Banner */}
                <Card className={difference !== 0 ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200' : ''}>
                  <CardContent className="py-4">
                    <div className="flex items-center gap-3">
                      {difference !== 0 ? (
                        <TrendingDown className="w-8 h-8 text-green-600" />
                      ) : (
                        <Scale className="w-8 h-8 text-gray-400" />
                      )}
                      <div>
                        <p className="font-semibold text-gray-900">
                          {difference === 0 ? 'Kainos vienodos' : 
                           difference > 0 ? 'Gamtos Ateitis pigiau' : 'Žaliasis Taškas pigiau'}
                        </p>
                        {difference !== 0 && (
                          <p className="text-sm text-gray-600">
                            Sutaupysite <strong className="text-green-600">{Math.abs(difference).toFixed(2)} €</strong> per metus
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Detailed Breakdown */}
                <Tabs defaultValue="comparison" className="w-full">
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="comparison">Palyginimas</TabsTrigger>
                    <TabsTrigger value="gamtosAteitis">Gamtos Ateitis</TabsTrigger>
                    <TabsTrigger value="zaliasisTaskas">Žaliasis Taškas</TabsTrigger>
                  </TabsList>

                  <TabsContent value="comparison">
                    <Card>
                      <CardHeader>
                        <CardTitle>Detalus palyginimas pagal pakuotės tipą</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b">
                                <th className="text-left py-2 font-medium">Pakuotė</th>
                                <th className="text-right py-2 font-medium">Kiekis (t)</th>
                                <th className="text-right py-2 font-medium">Gamtos Ateitis</th>
                                <th className="text-right py-2 font-medium">Žaliasis Taškas</th>
                                <th className="text-right py-2 font-medium">Skirtumas</th>
                              </tr>
                            </thead>
                            <tbody>
                              {results.gamtosAteitis.details.map((item, idx) => {
                                const ztItem = results.zaliasisTaskas.details[idx];
                                const gaTotal = item.cost + item.tax;
                                const ztTotal = ztItem.cost + ztItem.tax;
                                const diff = ztTotal - gaTotal;
                                const isNonRecyclable = item.id.includes('NonRecyclable');
                                return (
                                  <tr key={item.type} className={`border-b last:border-0 ${isNonRecyclable ? 'bg-orange-50' : ''}`}>
                                    <td className="py-2">{item.type}</td>
                                    <td className="py-2 text-right">{item.quantity.toFixed(3)}</td>
                                    <td className="py-2 text-right">{gaTotal.toFixed(2)} €</td>
                                    <td className="py-2 text-right">{ztTotal.toFixed(2)} €</td>
                                    <td className={`py-2 text-right font-medium ${diff > 0 ? 'text-green-600' : diff < 0 ? 'text-red-600' : ''}`}>
                                      {diff > 0 ? '+' : ''}{diff.toFixed(2)} €
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                            <tfoot>
                              <tr className="font-bold border-t-2">
                                <td className="py-2">VISO</td>
                                <td></td>
                                <td className="py-2 text-right">{totalGA.toFixed(2)} €</td>
                                <td className="py-2 text-right">{totalZT.toFixed(2)} €</td>
                                <td className={`py-2 text-right ${difference > 0 ? 'text-green-600' : difference < 0 ? 'text-red-600' : ''}`}>
                                  {difference > 0 ? '+' : ''}{difference.toFixed(2)} €
                                </td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>

                  <TabsContent value="gamtosAteitis">
                    <Card>
                      <CardHeader>
                        <CardTitle>Gamtos Ateitis - detalės</CardTitle>
                        <CardDescription>
                          <a href="https://gamtosateitis.lt" target="_blank" rel="noopener noreferrer" className="text-green-600 hover:underline">
                            gamtosateitis.lt
                          </a>
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {results.gamtosAteitis.details.map((item) => (
                            <div key={item.type} className={`p-3 rounded-lg ${item.id.includes('NonRecyclable') ? 'bg-orange-50' : 'bg-gray-50'}`}>
                              <div className="flex justify-between items-start mb-2">
                                <span className="font-medium">{item.type}</span>
                                <span className="font-bold">{(item.cost + item.tax).toFixed(2)} €</span>
                              </div>
                              <div className="text-sm text-gray-600 space-y-1">
                                <div className="flex justify-between">
                                  <span>Kiekis:</span>
                                  <span>{item.quantity.toFixed(3)} t</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>Kaina už {item.pricing.unit === 'tiekta' ? 'tiektą' : 'tvarkomą'} t:</span>
                                  <span>{item.pricing.price} €/t</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>LRV užduotis:</span>
                                  <span>{item.pricing.duty}%</span>
                                </div>
                                {item.tax > 0 && (
                                  <div className="flex justify-between text-orange-600">
                                    <span>Taršos mokestis:</span>
                                    <span>{item.tax.toFixed(2)} €</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>

                  <TabsContent value="zaliasisTaskas">
                    <Card>
                      <CardHeader>
                        <CardTitle>Žaliasis Taškas - detalės</CardTitle>
                        <CardDescription>
                          <a href="https://www.zaliasistaskas.lt" target="_blank" rel="noopener noreferrer" className="text-green-600 hover:underline">
                            zaliasistaskas.lt
                          </a>
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {results.zaliasisTaskas.details.map((item) => (
                            <div key={item.type} className={`p-3 rounded-lg ${item.id.includes('NonRecyclable') ? 'bg-orange-50' : 'bg-gray-50'}`}>
                              <div className="flex justify-between items-start mb-2">
                                <span className="font-medium">{item.type}</span>
                                <span className="font-bold">{(item.cost + item.tax).toFixed(2)} €</span>
                              </div>
                              <div className="text-sm text-gray-600 space-y-1">
                                <div className="flex justify-between">
                                  <span>Kiekis:</span>
                                  <span>{item.quantity.toFixed(3)} t</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>Kaina už tvarkomą t:</span>
                                  <span>{item.pricing.price} €/t</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>LRV užduotis:</span>
                                  <span>{item.pricing.duty}%</span>
                                </div>
                                {item.tax > 0 && (
                                  <div className="flex justify-between text-orange-600">
                                    <span>Taršos mokestis:</span>
                                    <span>{item.tax.toFixed(2)} €</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>
                </Tabs>

                {/* Explanation */}
                <Card className="bg-amber-50 border-amber-200">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-amber-800">
                      <Lightbulb className="w-5 h-5" />
                      Kaip skaičiuojama?
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm text-amber-900 space-y-3">
                    <p><strong>Tvarkymo kaina</strong> priklauso nuo to, ar organizacija taiko kainą už <em>tiektą</em> ar <em>tvarkomą</em> toną:</p>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li><strong>Tiekta tona</strong> = mokate už visą į rinką tiektą kiekį</li>
                      <li><strong>Tvarkoma tona</strong> = mokate tik už faktiškai tvarkomą dalį</li>
                    </ul>
                    <p><strong>Taršos mokestis</strong> taikomas plastikinėms, PET ir kombinuotoms pakuotėms už nesutvarkytą dalį.</p>
                    <p><strong>Neperdirbamos pakuotės</strong> - už jas mokamas pilnas taršos mokestis (100%).</p>
                    <p className="text-amber-700">
                      <strong>Pastaba:</strong> Šie skaičiavimai orientaciniai. Tikslias kainas derinkite su organizacijomis.
                    </p>
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        </div>
          </TabsContent>

          {/* ======================================================= */}
          {/* TAB 2: GIA – Apmokestinamieji gaminiai */}
          {/* ======================================================= */}
          <TabsContent value="gia">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-blue-900">GIA – Gamintojų ir importuotojų asocijacija</h4>
                  <p className="text-sm text-blue-800 mt-1">
                    GIA tvarko <strong>apmokestinamuosius gaminius</strong>: alyvas, padangas, filtrus ir amortizatorius – tai skirtingi atliekų srautai nei pakuotės.
                    Kainos taikomos už <strong>tiektą toną į LR vidaus rinką</strong>. Papildomai taikomas metinis administravimo mokestis.
                  </p>
                  <a href="https://gia.lt/gamintojams-ir-importuotojams/skaiciuokle/" target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline mt-1 inline-block">
                    gia.lt – oficiali skaičiuoklė ↗
                  </a>
                </div>
              </div>
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
              {/* GIA Input */}
              <div className="lg:col-span-1 space-y-6">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Settings className="w-5 h-5 text-blue-600" />
                      Gaminių kiekiai (tonomis)
                    </CardTitle>
                    <CardDescription>2025 m. kainos be PVM</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Oils */}
                    <div className="pb-3 border-b border-gray-100">
                      <div className="flex items-center gap-2 mb-2">
                        <Droplets className="w-4 h-4 text-blue-500" />
                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Alyvos</span>
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="oils" className="text-sm">Alyvos (variklo, pavarų, pramoninė)</Label>
                        <div className="flex items-center gap-2">
                          <Input id="oils" type="number" min="0" step="0.001" placeholder="0"
                            value={giaQuantities.oils}
                            onChange={e => handleGiaQuantityChange('oils', e.target.value)}
                            className="flex-1" />
                          <span className="text-sm text-gray-500 w-8">t</span>
                        </div>
                        <p className="text-xs text-gray-400">6,00 €/t</p>
                      </div>
                    </div>

                    {/* Tires */}
                    <div className="pb-3 border-b border-gray-100">
                      <div className="flex items-center gap-2 mb-2">
                        <Car className="w-4 h-4 text-gray-500" />
                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Padangos</span>
                      </div>
                      <div className="space-y-3">
                        <div className="space-y-1">
                          <Label htmlFor="tiresLight" className="text-sm">Motociklų ir lengvųjų automobilių</Label>
                          <div className="flex items-center gap-2">
                            <Input id="tiresLight" type="number" min="0" step="0.001" placeholder="0"
                              value={giaQuantities.tiresLight}
                              onChange={e => handleGiaQuantityChange('tiresLight', e.target.value)}
                              className="flex-1" />
                            <span className="text-sm text-gray-500 w-8">t</span>
                          </div>
                          <p className="text-xs text-gray-400">148,16 €/t</p>
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor="tiresHeavy" className="text-sm">Autobusų, krovininių, žemės ūkio (&gt;3 kg)</Label>
                          <div className="flex items-center gap-2">
                            <Input id="tiresHeavy" type="number" min="0" step="0.001" placeholder="0"
                              value={giaQuantities.tiresHeavy}
                              onChange={e => handleGiaQuantityChange('tiresHeavy', e.target.value)}
                              className="flex-1" />
                            <span className="text-sm text-gray-500 w-8">t</span>
                          </div>
                          <p className="text-xs text-gray-400">165,11 €/t</p>
                        </div>
                      </div>
                    </div>

                    {/* Filters & Shock absorbers */}
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Settings className="w-4 h-4 text-gray-500" />
                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Filtrai ir amortizatoriai</span>
                      </div>
                      <div className="space-y-3">
                        {[
                          { id: 'filtersFuel', label: 'Filtrai – degalų arba tepalų', rate: '622,87 €/t' },
                          { id: 'filtersAir',  label: 'Filtrai – įsiurbiamo oro',      rate: '623,77 €/t' },
                          { id: 'shockAbsorbers', label: 'Amortizatoriai',             rate: '336,15 €/t' },
                        ].map(item => (
                          <div key={item.id} className="space-y-1">
                            <Label htmlFor={item.id} className="text-sm">{item.label}</Label>
                            <div className="flex items-center gap-2">
                              <Input id={item.id} type="number" min="0" step="0.001" placeholder="0"
                                value={giaQuantities[item.id]}
                                onChange={e => handleGiaQuantityChange(item.id, e.target.value)}
                                className="flex-1" />
                              <span className="text-sm text-gray-500 w-8">t</span>
                            </div>
                            <p className="text-xs text-gray-400">{item.rate}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Button variant="outline" className="w-full" onClick={clearGia}>
                  Išvalyti viską
                </Button>
              </div>

              {/* GIA Results */}
              <div className="lg:col-span-2 space-y-6">
                {giaResults.details.length === 0 ? (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <Settings className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500">Įveskite gaminių kiekius kairėje, kad pamatytumėte GIA mokesčius</p>
                    </CardContent>
                  </Card>
                ) : (
                  <>
                    {/* Summary Card */}
                    <Card className="border-blue-200 bg-blue-50">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-blue-900">GIA – Mokesčių santrauka</CardTitle>
                        <CardDescription>
                          <a href="https://gia.lt" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">gia.lt</a>
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-700">Atliekų tvarkymo mokestis:</span>
                            <span className="font-medium">{giaResults.subtotal.toFixed(2)} €</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-700">Metinis administravimo mokestis:</span>
                            <span className="font-medium">{giaResults.adminFee.toFixed(2)} €</span>
                          </div>
                          <div className="border-t border-blue-200 pt-2 flex justify-between">
                            <span className="font-bold text-blue-900">Viso:</span>
                            <span className="font-bold text-xl text-blue-900">{giaResults.total.toFixed(2)} €</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Detailed breakdown */}
                    <Card>
                      <CardHeader>
                        <CardTitle>Detalus skaičiavimas</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b">
                                <th className="text-left py-2 font-medium">Gaminys</th>
                                <th className="text-right py-2 font-medium">Kiekis (t)</th>
                                <th className="text-right py-2 font-medium">Kaina (€/t)</th>
                                <th className="text-right py-2 font-medium">Suma (€)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {giaResults.details.map(item => (
                                <tr key={item.id} className="border-b last:border-0">
                                  <td className="py-2">{item.name}</td>
                                  <td className="py-2 text-right">{item.qty.toFixed(3)}</td>
                                  <td className="py-2 text-right">{item.price.toFixed(2)}</td>
                                  <td className="py-2 text-right font-medium">{item.cost.toFixed(2)}</td>
                                </tr>
                              ))}
                              <tr className="border-t text-gray-500">
                                <td colSpan={3} className="py-2 italic">Metinis administravimo mokestis</td>
                                <td className="py-2 text-right">{giaResults.adminFee.toFixed(2)}</td>
                              </tr>
                            </tbody>
                            <tfoot>
                              <tr className="font-bold border-t-2">
                                <td colSpan={3} className="py-2">VISO</td>
                                <td className="py-2 text-right text-blue-700">{giaResults.total.toFixed(2)} €</td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Admin fee bracket info */}
                    <Card className="bg-amber-50 border-amber-200">
                      <CardHeader className="pb-2">
                        <CardTitle className="flex items-center gap-2 text-amber-800 text-base">
                          <Lightbulb className="w-4 h-4" />
                          Metinis administravimo mokestis
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-amber-900 mb-3">
                          Mokestis priklauso nuo bendros atliekų tvarkymo sumos per metus:
                        </p>
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs text-amber-900">
                            <thead>
                              <tr className="border-b border-amber-200">
                                <th className="text-left py-1">Nuo</th>
                                <th className="text-left py-1">Iki</th>
                                <th className="text-right py-1">Mokestis</th>
                              </tr>
                            </thead>
                            <tbody>
                              {GIA_ADMIN_FEE.filter(b => b.to !== Infinity).map((b, i) => (
                                <tr key={i} className={`border-b border-amber-100 ${giaResults.subtotal >= b.from && giaResults.subtotal <= b.to ? 'bg-amber-200 font-bold' : ''}`}>
                                  <td className="py-1">{b.from} €</td>
                                  <td className="py-1">{b.to} €</td>
                                  <td className="py-1 text-right">{b.fee} €</td>
                                </tr>
                              ))}
                              <tr className={`${giaResults.subtotal > 10000 ? 'bg-amber-200 font-bold' : ''}`}>
                                <td className="py-1">Nuo 10 001 €</td>
                                <td className="py-1">–</td>
                                <td className="py-1 text-right">1 540 €</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        <p className="text-xs text-amber-700 mt-3">
                          <strong>Pastaba:</strong> Skaičiavimai orientaciniai. Tikslias sąlygas rasite{' '}
                          <a href="https://gia.lt" target="_blank" rel="noopener noreferrer" className="underline">gia.lt</a>
                        </p>
                      </CardContent>
                    </Card>
                  </>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default PackagingCalculator;
